import type { PressableProperties } from "@mindlercare/log-washer-client";
import { useAuth } from "@mindlercare/auth-ui";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useCurrentCountryId } from "core/selectors/marketSelectors";

type KeysBrowserActionsDontCareAbout = "meta" | "osVersion" | "appVersion";

type DistributiveOmit<T, K extends keyof T> = T extends unknown
  ? Omit<T, K>
  : never;

type EventMetaData = {
  appVersion: string;
  deviceType: "browser";
  market: number;
  service: "mindlercare";
  fromPage: string;
};

const useEventMetaData = (): EventMetaData => {
  const market = useCurrentCountryId();
  const { pathname: fromPage } = useLocation();

  return useMemo(
    () => ({
      deviceType: "browser",
      appVersion: "",
      service: "mindlercare",
      market,
      fromPage,
    }),
    [market, fromPage],
  );
};

const usePressableEvent = (
  props: DistributiveOmit<PressableProperties, keyof EventMetaData>,
): ((pressableId?: string) => void) => {
  const { userId, jwt } = useAuth();
  const eventMetaData = useEventMetaData();

  return useCallback(
    (pressableId?: string) => {
      if (!userId || !jwt) return;
      void import("@mindlercare/log-washer-client").then(
        ({ default: logEvent }) =>
          logEvent(
            `${userId}`,
            {
              name: "Pressable Pressed",
              event_version: 1,
              properties: {
                ...props,
                ...eventMetaData,
                pressableId: pressableId ?? props.pressableId,
              },
            },
            {
              authorizationKey: `JWT ${jwt}`,
              logEndpoint: `${import.meta.env.VITE_BASE_URL}eventloki/log`,
            },
          )?.catch(console.error),
      );
    },
    [userId, jwt, props, eventMetaData],
  );
};

export const useButtonEvent = (
  props: Omit<
    Extract<PressableProperties, { type: "button" }>,
    keyof EventMetaData | KeysBrowserActionsDontCareAbout | "type" | "action"
  >,
): ((pressableId?: string) => void) => {
  return usePressableEvent({
    ...props,
    type: "button",
    action: undefined,
  });
};
